import React, { ReactElement, useMemo, useState } from 'react';
import ExportCoachesModal from './ExportCoachesModal';
import LicencesCell from './LicencesCell';
import AppLayout from 'components/layouts/AppLayout';
import Table, {
    createDetailLinkTh,
    createTd,
    createTh,
} from 'components/tables/Table';
import {
    ColumnHeadInterface,
    ColumnSize,
    ColumnValueInterface,
    ColumnValueType,
    ColumnValueWeight,
    FiltersInterface,
    FilterSize,
    Option,
} from 'components/interfaces/GeneralInterface';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import styles from 'components/pages/base/list/ListPage.module.scss';
import { PaginatorPageInterface } from 'components/interfaces/Paginator';
import { getRoute } from 'constants/routes';
import {
    CoachListApiInterface,
    FieldTypeApiEnum,
} from 'submodules/api_middleware';
import { connectStrings } from 'utils/helpers/browser/string';
import { findCoachLicenceTypes } from 'utils/helpers/browser/filter';
import { TNamespaceEnum } from 'types/types';
import { getMainFilter } from 'components/tables/filters/Filters';
import { validateSearchQuery } from 'utils/helpers/validateSearchQuery';

interface Props extends PaginatorPageInterface {
    coaches: CoachListApiInterface[];
    regionList: Option[];
}

const regionLabel = (code: string | null, list: Option[]): string => {
    if (!code) {
        return '—';
    }
    const opt = list.find((o) => String(o.value) === String(code));
    return opt?.label ?? code;
};

const CoachesPage = ({
    coaches,
    paginator,
    regionList,
}: Props): ReactElement => {
    const { t } = useTranslation();
    const [exportModal, setExportModal] = useState<boolean>(false);

    const filterConfig: FiltersInterface = useMemo(
        () => ({
            fixed: {
                mainField: getMainFilter(
                    'Trainers.searchPlaceholder',
                    'query',
                    (value) => validateSearchQuery(value as string, t)
                ),
                showAdvancedBtn: true,
            },
            advanced: [
                {
                    name: 'licence_type_id',
                    label: 'RefereeProfile.licenceType',
                    type: FieldTypeApiEnum.SELECT,
                    initValuesCb: () => findCoachLicenceTypes(t),
                    size: FilterSize.FOURTH,
                },
                {
                    name: 'licence_number',
                    label: getTranslationKey(
                        TNamespaceEnum.person,
                        'licenceNumber'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.FOURTH,
                },
                {
                    name: 'region_code',
                    label: getTranslationKey(TNamespaceEnum.person, 'region'),
                    type: FieldTypeApiEnum.SELECT,
                    values: regionList,
                    size: FilterSize.FOURTH,
                },
            ],
        }),
        [t, regionList]
    );

    const columns: ColumnHeadInterface[] = useMemo<ColumnHeadInterface[]>(
        () => [
            createDetailLinkTh((id: ColumnValueType) =>
                getRoute('/coaches/{0}', [String(id)])
            ),
            createTh(t, 'CoachPage.columns.fullName', ColumnSize.M),
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'idName'),
                ColumnSize.M
            ),
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'region'),
                ColumnSize.M
            ),
            createTh(t, 'CoachPage.columns.credits', ColumnSize.S),
            createTh(t, 'CoachPage.columns.licencesOverview', ColumnSize.L),
            createTh(
                t,
                getTranslationKey(TNamespaceEnum.person, 'age'),
                ColumnSize.S
            ),
            createTh(t, 'CoachPage.columns.state', ColumnSize.M),
        ],
        [t]
    );

    const persons = coaches || [];
    const data: ColumnValueInterface[][] = persons.map(
        (coach: CoachListApiInterface) => {
            const creditsDisplay =
                coach.credits_count != null && coach.credits_count !== undefined
                    ? String(coach.credits_count)
                    : '—';

            const stateLabel = getTranslation(
                t,
                TNamespaceEnum.person,
                coach.is_engaged ? 'engaged' : 'notEngaged'
            );

            return [
                createTd(coach.publish_id),
                createTd(
                    connectStrings(' ', coach.first_name, coach.last_name) ||
                        '—',
                    ColumnValueWeight.BOLD
                ),
                createTd(coach.internal_id ?? '—'),
                createTd(regionLabel(coach.region_code, regionList)),
                createTd(creditsDisplay),
                createTd(<LicencesCell licences={coach.licences ?? []} />),
                createTd(
                    coach.age != null && coach.age !== undefined
                        ? String(coach.age)
                        : '—'
                ),
                createTd(stateLabel),
            ];
        }
    );

    return (
        <AppLayout
            pageTitle={{
                primaryBoldText: t('pages.coaches'),
                text: t('Trainers.findText'),
                isDark: true,
            }}
        >
            <>
                {exportModal && (
                    <ExportCoachesModal onHide={() => setExportModal(false)} />
                )}
                <Table
                    columns={columns}
                    data={data}
                    className={styles.table}
                    filterConfig={filterConfig}
                    paginator={paginator}
                    actions={[
                        {
                            type: 'export',
                            onClick: () => setExportModal(true),
                        },
                    ]}
                    showRowsCount
                />
            </>
        </AppLayout>
    );
};

export default CoachesPage;
